/**
 * Fetches product data for the given product id from the catalog API.
 *
 * @param id - The product identifier used as the `productId` query filter
 * @returns The parsed JSON response containing product data, or `undefined` if the request fails
 */
export async function getProductDataById(id: any) {
  try {
    const response = await fetch(
      `/api/catalog_system/pub/products/search?fq=productId:${id}`
    );

    return await response.json();
  } catch (e) {
    console.log('[DT] Erro ao buscar dados do produto', e);
  }
}

export function getItemShippingTier(orderForm: any, item: any) {
  const info = orderForm?.shippingData?.logisticsInfo?.find(
    (li: any) => li.itemId === item.id
  );

  const selectedSla = info?.slas?.find((sla: any) => sla.id === info.selectedSla);
  return selectedSla?.pickupStoreInfo?.friendlyName
    ? `retirada em loja: ${selectedSla.pickupStoreInfo.friendlyName}`
    : selectedSla?.name
      ? `Receba em casa: ${selectedSla.name}`
      : null;
}

export async function getItemSku(item: any) {
  const product = await getProductDataById(item.productId || item.productID);

  if (!product.length) return null;

  return product[0].items.find((i: any) => i.name === item.skuName)?.itemId ?? null;
}

export function getDiscount(item: any) {
  return item.discount ? item.discount :
    (
      parseFloat(Math.max(item.listPrice - item.sellingPrice, 0).toFixed(2)) / 100 || 0
    );
}

export function getItemCategory(item: any) {
  return item?.productCategories?.['39'] === 'BAZAR' ? 'Sale' : 'Coleção'
}

export function getItemCategory2(item: any) {
  const strategies = [
    () => Object.values(item?.productCategories || {}).pop(), // checkout select_item context
    () => item?.category?.split?.('>')?.[1]?.trim?.(), // deco select_item context
  ];

  for (const getValue of strategies) {
    const value = getValue();

    if (value !== null && value !== undefined) {
      return value;
    }
  }

  return null;
}

export function getItemCategory4(item: any) {
  const tags = item?.additionalInfo?.tags || item?.tags;
  return Array.isArray(tags) && tags.length ? tags.join(',') : null;
}

const BRAND_CODES = {
  farm: 'farmrio',
  'farm etc': 'farmetc',
  'farm rio': 'farmrio',
} as const;

type Standardized = keyof typeof BRAND_CODES
interface BrandItem {
  brand?: string;
  additionalInfo?: {
    brandName?: string;
  };
}

/**
 * Extrai o código padronizado (ex: 'farmrio') de uma única string de marca.
 * Retorna null se não houver mapeamento.
 */
function getBrandCode(brand: string | undefined): string | null {
  if (!brand) return null;
  const standardized = brand.toLowerCase().trim();
  return BRAND_CODES[standardized as Standardized] || null;
}

/**
 * Função principal que padroniza a marca ou lista de marcas para o formato final:
 * 'farmrio', 'farmetc', ou 'farmrio_farmetc'.
 */
export function normalizeBrand(input: string | BrandItem[] | BrandItem | string[] | undefined | null) {
  let brandStrings = [];

  if (Array.isArray(input) && input.every(item => typeof item === 'string')) {
    brandStrings = input as string[];
  } else if (Array.isArray(input)) {
    // Se for um Array, coleta a string da marca de cada item.
    brandStrings = input.map(item => item.additionalInfo?.brandName || item.brand);
  } else if (typeof input === 'object' && input !== null && 'brand' in input) {
    // Se for um Objeto único, trata como um Array de um único item (para reuso de lógica).
    brandStrings = [(input as BrandItem).additionalInfo?.brandName || (input as BrandItem).brand];
  } else if (typeof input === 'string') {
    // Se for uma String (mal formatada ou não), trata diretamente.
    return getBrandCode(input);
  } else {
    // Se for undefined, null, ou outro tipo não esperado.
    return null;
  }

  const brandCodes = brandStrings
    .map(getBrandCode)
    .filter((code): code is string => code !== null); // ['farmrio', 'farmetc', 'farmrio']

  const uniqueSortedCodes = [...new Set(brandCodes)].sort(); // ['farmetc', 'farmrio']

  // Retorna a combinação ('farmrio', 'farmetc', ou 'farmrio_farmetc').
  return uniqueSortedCodes.length > 0 ? uniqueSortedCodes.join('_') : null;
}
